// Admin > Users / Vendors: delete an account. Its open contracts are listed and must be closed
// (terminated) first; open bookings are cancelled, offerings hidden, and a Marketing lead the
// vendor came from becomes a lead again. The account's history stays in the audit log.
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Checkbox, FormControlLabel, TextField, LinearProgress,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { USER_DELETION_CHECK_QUERY, DELETE_USER_MUTATION } from '../graphql/queries';

const rupees = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

export default function DeleteUserDialog({ user, onClose, onDeleted }) {
  const { data, loading, error } = useQuery(USER_DELETION_CHECK_QUERY, { variables: { userId: user.id }, fetchPolicy: 'network-only' });
  const [run, { loading: deleting, error: deleteError }] = useMutation(DELETE_USER_MUTATION);
  const [closeContracts, setCloseContracts] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [reason, setReason] = useState('');
  const c = data?.userDeletionCheck;
  const hasContracts = (c?.contracts || []).length > 0;
  const canDelete = c && !c.blockers.length && understood && (!hasContracts || closeContracts);

  const go = async () => {
    const { data: d } = await run({ variables: { userId: user.id, closeContracts: hasContracts && closeContracts, reason: reason.trim() || null } }).catch(() => ({}));
    if (d) onDeleted(d.deleteUser);
  };

  return (
    <Dialog open onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center"><DeleteForeverIcon color="error" /><span>Delete {user.email}?</span></Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {c && (
          <Stack spacing={2}>
            <Typography variant="body2">
              <b>{c.name || c.email}</b> · {c.role}{c.isActive ? '' : ' · deactivated'}
            </Typography>
            {c.blockers.map(b => <Alert key={b} severity="error">{b}</Alert>)}

            {hasContracts ? (
              <>
                <Alert severity="warning">
                  This account is in {c.contracts.length} open contract{c.contracts.length === 1 ? '' : 's'}. They must be closed before the account can be deleted.
                </Alert>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Contract</TableCell><TableCell>Status</TableCell><TableCell align="right">Value</TableCell></TableRow></TableHead>
                  <TableBody>
                    {c.contracts.map(k => (
                      <TableRow key={k.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{k.ref || 'No ref'}</Typography>
                          <Typography variant="caption" color="text.secondary">{k.title}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={k.status.replace(/_/g, ' ')} color={k.signed ? 'success' : 'default'} variant="outlined" /></TableCell>
                        <TableCell align="right">{rupees(k.cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <FormControlLabel
                  control={<Checkbox checked={closeContracts} onChange={e => setCloseContracts(e.target.checked)} />}
                  label={<Typography variant="body2">Close (terminate) {c.contracts.length === 1 ? 'this contract' : `these ${c.contracts.length} contracts`} first</Typography>}
                />
              </>
            ) : <Alert severity="info">This account has no open contracts.</Alert>}

            {(c.openBookings > 0 || c.activeOfferings > 0 || c.leadName) && (
              <Typography variant="body2" component="div">
                Also:
                <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                  {c.openBookings > 0 && <li>{c.openBookings} open booking{c.openBookings === 1 ? '' : 's'} will be cancelled (the other side is notified)</li>}
                  {c.activeOfferings > 0 && <li>{c.activeOfferings} published offering{c.activeOfferings === 1 ? '' : 's'} will be hidden from families</li>}
                  {c.leadName && <li>The Marketing lead "{c.leadName}" becomes a lead again, so the vendor can be set up again</li>}
                </ul>
              </Typography>
            )}
            <TextField size="small" label="Reason (optional, kept in the audit log)" value={reason} onChange={e => setReason(e.target.value)} fullWidth />
            <FormControlLabel
              control={<Checkbox checked={understood} onChange={e => setUnderstood(e.target.checked)} />}
              label={<Typography variant="body2">I understand the account is removed: it can no longer sign in and its email can be used for a new account. Its history stays in the audit log.</Typography>}
            />
            {deleteError && <Alert severity="error">{deleteError.message}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button variant="contained" color="error" startIcon={<DeleteForeverIcon />} disabled={!canDelete || deleting} onClick={go}>
          {deleting ? 'Deleting…' : hasContracts ? 'Close contracts and delete' : 'Delete account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
